'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useTheme } from 'next-themes';
import { Sun, Moon, LogOut, User, Menu, X } from 'lucide-react';
import { useUser, auth } from '@/firebase';
import { signOut } from 'firebase/auth';
import { useRouter } from 'next/navigation';

export default function Navbar() {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { theme, setTheme } = useTheme();
  const { user, loading } = useUser();
  const router = useRouter();

  useEffect(() => {
    const hasSeenTooltip = localStorage.getItem('hasSeenThemeTooltip');
    if (!hasSeenTooltip) {
      const showTimer = setTimeout(() => setShowTooltip(true), 1000);
      const hideTimer = setTimeout(() => {
        setShowTooltip(false);
        localStorage.setItem('hasSeenThemeTooltip', 'true');
      }, 6000);
      return () => {
        clearTimeout(showTimer);
        clearTimeout(hideTimer);
      };
    }
  }, []);

  useEffect(() => {
    const handleScroll = (e?: Event) => {
      // Check both window scroll and scroll containers
      const scrollElement = e?.target as HTMLElement | null;
      const currentScrollY = scrollElement?.scrollTop ?? window.scrollY;

      if (currentScrollY < lastScrollY) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      }
      setLastScrollY(currentScrollY);
    };

    // Listen to window scroll
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Also listen to scroll containers (pages with h-screen overflow-y-auto)
    const scrollContainers = document.querySelectorAll('.h-screen.overflow-y-auto, [class*="overflow-y-auto"]');
    scrollContainers.forEach(container => {
      container.addEventListener('scroll', handleScroll, { passive: true });
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      scrollContainers.forEach(container => {
        container.removeEventListener('scroll', handleScroll);
      });
    };
  }, [lastScrollY]);

  // Click outside to close user menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      router.push('/web');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ease-in-out px-6 lg:px-8 pt-4 ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
    >
      <div className="max-w-6xl mx-auto">
        <div className="backdrop-blur-xl bg-card/80 dark:bg-background/80 border border-border rounded-2xl shadow-lg shadow-black/20">
          <div className="px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Logo */}
              <Link href="/web" className="flex items-center gap-2">
                <span className="text-lg font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
                  TradeAte
                </span>
              </Link>

              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center gap-8">
                <Link
                  href="/web/features"
                  className="text-sm text-foreground/70 hover:text-foreground transition"
                >
                  Features
                </Link>
                <Link
                  href="/web/pricing"
                  className="text-sm text-foreground/70 hover:text-foreground transition"
                >
                  Pricing
                </Link>
                <Link
                  href="/web/feedback"
                  className="text-sm text-foreground/70 hover:text-foreground transition"
                >
                  Roadmap
                </Link>
                <Link
                  href="/web/brokers"
                  className="text-sm text-foreground/70 hover:text-foreground transition"
                >
                  Supported Brokers
                </Link>
                <Link
                  href="/web/indicator"
                  className="text-sm text-foreground/70 hover:text-foreground transition"
                >
                  Indicator
                </Link>
              </nav>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-muted transition-colors"
                aria-label="Toggle mobile menu"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6 text-foreground/70" />
                ) : (
                  <Menu className="h-6 w-6 text-foreground/70" />
                )}
              </button>

              {/* Right Side Actions */}
              <div className="flex items-center gap-3">
               
                {/* Theme Toggle */}
                <div className="relative">
                  <button
                    onClick={() => {
                      setTheme(theme === 'dark' ? 'light' : 'dark');
                      setShowTooltip(false);
                    }}
                    className="p-2 rounded-lg hover:bg-muted transition-colors"
                    aria-label="Toggle theme"
                  >
                    <Sun className="h-5 w-5 dark:hidden text-foreground/70" />
                    <Moon className="h-5 w-5 hidden dark:block text-foreground/70" />
                  </button>
                  {showTooltip && (
                    <div
                      className="absolute -bottom-14 right-0"
                      style={{
                        animation: showTooltip
                          ? 'slideIn 0.3s ease-out forwards'
                          : 'slideOut 0.3s ease-in forwards',
                      }}
                    >
                      <div className="absolute -top-2 right-4 w-4 h-4 bg-white dark:bg-gray-800 rotate-45 border-l border-t border-border" />
                      <div className="relative bg-white dark:bg-gray-800 text-foreground px-4 py-2 rounded-lg shadow-xl border border-border whitespace-nowrap">
                        <span className="text-sm font-medium">
                          Switch to Light Mode ☀️
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Auth Buttons */}
                {loading ? (
                  // Loading state
                  <div className="w-20 h-8 bg-foreground/10 rounded-full animate-pulse" />
                ) : user ? (
                  // Logged in - Show Dashboard + User Menu
                  <>
                    <Link
                      href="/dashboard"
                      className="hidden sm:inline text-sm text-foreground/70 hover:text-foreground transition"
                    >
                      Dashboard
                    </Link>
                    
                    {/* User Menu */}
                    <div className="relative" ref={userMenuRef}>
                      <button
                        onClick={() => setShowUserMenu(!showUserMenu)}
                        className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted transition-colors"
                        aria-label="User menu"
                      >
                        <User className="h-5 w-5 text-foreground/70" />
                      </button>

                      {/* Dropdown Menu */}
                      {showUserMenu && (
                        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-border py-2">
                          <div className="px-4 py-2 border-b border-border">
                            <p className="text-sm font-medium text-foreground truncate">
                              {user.email}
                            </p>
                          </div>
                          <Link
                            href="/dashboard"
                            className="block px-4 py-2 text-sm text-foreground/70 hover:bg-muted transition"
                            onClick={() => setShowUserMenu(false)}
                          >
                            Dashboard
                          </Link>
                          <button
                            onClick={() => {
                              handleLogout();
                              setShowUserMenu(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-muted transition flex items-center gap-2"
                          >
                            <LogOut className="h-4 w-4" />
                            Sign out
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  // Not logged in - Show Log in + Sign up
                  <>
                    <Link
                      href="/login?redirect=/web"
                      className="hidden sm:inline text-sm text-foreground/70 hover:text-foreground transition"
                    >
                      Log in
                    </Link>
                    <Link
                      href="/login?redirect=/web/pricing"
                      className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2 text-sm font-medium text-background hover:bg-foreground/90 transition"
                    >
                      <span>Sign up</span>
                    </Link>
                  </>
                )}
              </div>
            </div>

            {/* Mobile Menu Dropdown */}
            {mobileMenuOpen && (
              <div className="md:hidden border-t border-border px-6 py-4">
                <nav className="flex flex-col gap-4">
                  <Link
                    href="/web/features"
                    className="text-sm text-foreground/70 hover:text-foreground transition py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Features
                  </Link>
                  <Link
                    href="/web/pricing"
                    className="text-sm text-foreground/70 hover:text-foreground transition py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Pricing
                  </Link>
                  <Link
                    href="/web/feedback"
                    className="text-sm text-foreground/70 hover:text-foreground transition py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Roadmap
                  </Link>
                  <Link
                    href="/web/brokers"
                    className="text-sm text-foreground/70 hover:text-foreground transition py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Supported Brokers
                  </Link>
                  <Link
                    href="/web/indicator"
                    className="text-sm text-foreground/70 hover:text-foreground transition py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Indicator
                  </Link>
                  {!loading && !user && (
                    <Link
                      href="/login?redirect=/web"
                      className="text-sm text-foreground/70 hover:text-foreground transition py-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Log in
                    </Link>
                  )}
                </nav>
              </div>
            )}
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideOut {
          from {
            opacity: 1;
            transform: translateY(0);
          }
          to {
            opacity: 0;
            transform: translateY(-10px);
          }
        }
      `}</style>
    </header>
  );
}
